import { useRouter } from "expo-router";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  HeartHandshake,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sprout,
  Users,
} from "../../../components/lucide-icons";
import { ReactNode } from "react";
import {
  Image,
  ImageBackground,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

import { AppShell } from "../../../components/layout";
import { semantic, shadows } from "../../../theme/tokens";
import { monarchDemoSite } from "../data/monarch-demo-site";

const monarchHeroImage = require("../../../../assets/monarch-arrangement-hero.png");
const monarchBlueLogo = require("../../../../assets/monarch-blue-logo.png");

type DemoIcon = (props: { color: string; size: number; strokeWidth?: number }) => ReactNode;

const familyPaths = [
  {
    title: "Start arrangements online",
    body: "Begin with the details Monarch needs most, then a director reviews everything with you.",
    icon: Users,
    action: "Start now",
  },
  {
    title: "Plan ahead",
    body: "Record wishes, service preferences, and family contacts before there is an urgent need.",
    icon: Sprout,
    action: "Begin a plan",
  },
  {
    title: "Continue with Monarch",
    body: "Return to an existing arrangement using a private family access link.",
    icon: ShieldCheck,
    action: "Continue",
  },
] as const;

const serviceHighlights = [
  "Funeral and cremation arrangements",
  "Catholic and faith-based services",
  "Cremation authorization and signatures",
  "Digital service folders and guestbooks",
] as const;

export default function MonarchSiteDemoScreen() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 940;

  const openArrangementStart = () => router.push("/demo/monarch/arrangements/start" as never);

  return (
    <AppShell role="public" accessibilityLabel="Monarch public website">
      <ScrollView contentContainerStyle={styles.scroll} contentInsetAdjustmentBehavior="automatic">
        <View style={styles.shell}>
          <View style={[styles.header, isDesktop ? null : styles.headerMobile]}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Open Monarch home"
              onPress={() => router.push("/demo/monarch" as never)}
              style={styles.logoButton}
            >
              <Image source={monarchBlueLogo} resizeMode="contain" style={styles.logo} />
            </Pressable>
            <View style={[styles.nav, isDesktop ? null : styles.navMobile]}>
              <Text style={styles.navItem}>Services</Text>
              <Text style={styles.navItem}>Planning</Text>
              <Text style={styles.navItem}>Contact</Text>
            </View>
          </View>

          <View style={[styles.hero, isDesktop ? styles.heroDesktop : null]}>
            <View style={styles.heroCopy}>
              <View style={styles.careBadge}>
                <HeartHandshake color={semantic.action.primary} size={17} strokeWidth={1.9} />
                <Text style={styles.careBadgeText}>Monarch family care</Text>
              </View>
              <Text style={[styles.heroTitle, isDesktop ? styles.heroTitleDesktop : null]}>
                Funeral and cremation care, guided with clarity.
              </Text>
              <Text style={styles.heroBody}>
                Start online when you are ready. Monarch keeps the phone number, address, and care
                team close while your family moves through each decision privately.
              </Text>

              <View style={styles.heroActions}>
                <PrimaryAction label="Start Arrangements Online" onPress={openArrangementStart} />
                <SecondaryAction label={monarchDemoSite.phone} icon={Phone} onPress={() => undefined} />
              </View>

              <View style={styles.assuranceRow}>
                <Assurance icon={Clock3} label="Available 24/7" />
                <Assurance icon={ShieldCheck} label="Director reviewed" />
                <Assurance icon={MapPin} label="Margate, Florida" />
              </View>
            </View>

            <View style={styles.heroVisual}>
              <ImageBackground
                source={monarchHeroImage}
                resizeMode="cover"
                style={styles.heroImage}
                imageStyle={styles.heroImageRadius}
              >
                <View style={styles.heroImageScrim} />
                <View style={styles.serviceCard}>
                  <Text style={styles.serviceEyebrow}>Today at Monarch</Text>
                  <Text style={styles.serviceTitle}>Care team ready</Text>
                  <Text style={styles.serviceText}>
                    A family can start online, call the funeral home, or continue a private plan.
                  </Text>
                </View>
              </ImageBackground>
            </View>
          </View>

          <View style={styles.pathGrid}>
            {familyPaths.map((path, index) => (
              <PathCard
                key={path.title}
                icon={path.icon}
                title={path.title}
                body={path.body}
                action={path.action}
                featured={index === 0}
                onPress={openArrangementStart}
              />
            ))}
          </View>

          <View style={[styles.splitBand, isDesktop ? styles.splitBandDesktop : null]}>
            <View style={styles.bandCopy}>
              <Text style={styles.sectionKicker}>What Monarch can handle online</Text>
              <Text style={styles.sectionTitle}>A calm start that becomes director-ready paperwork.</Text>
              <Text style={styles.sectionBody}>
                Families answer what they can, upload what they have, and leave the rest for Monarch
                staff to confirm before any document is finalized.
              </Text>
            </View>
            <View style={styles.serviceList}>
              {serviceHighlights.map((item) => (
                <View key={item} style={styles.serviceRow}>
                  <CheckCircle2 color={semantic.status.success.foreground} size={18} strokeWidth={2} />
                  <Text style={styles.serviceRowText}>{item}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={[styles.contactBand, isDesktop ? null : styles.contactBandMobile]}>
            <View style={styles.contactCopy}>
              <Text style={styles.contactTitle}>Need immediate help?</Text>
              <Text style={styles.contactText}>
                Call Monarch directly or begin online so the care team has the essentials before the next conversation.
              </Text>
            </View>
            <View style={styles.contactActions}>
              <SecondaryAction label={monarchDemoSite.phone} icon={Phone} onPress={() => undefined} light />
              <PrimaryAction label="Begin Online" onPress={openArrangementStart} light />
            </View>
          </View>
        </View>
      </ScrollView>
    </AppShell>
  );
}

function PrimaryAction({
  label,
  onPress,
  light = false,
}: {
  label: string;
  onPress: () => void;
  light?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={[styles.primaryAction, light ? styles.primaryActionLight : null]}
    >
      <Text style={[styles.primaryActionText, light ? styles.primaryActionTextLight : null]}>{label}</Text>
      <ArrowRight color={light ? semantic.action.primary : semantic.text.inverse} size={19} strokeWidth={2} />
    </Pressable>
  );
}

function SecondaryAction({
  label,
  icon: Icon,
  onPress,
  light = false,
}: {
  label: string;
  icon: DemoIcon;
  onPress: () => void;
  light?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={[styles.secondaryAction, light ? styles.secondaryActionLight : null]}
    >
      <Icon color={light ? semantic.text.inverse : semantic.action.primary} size={18} strokeWidth={2} />
      <Text style={[styles.secondaryActionText, light ? styles.secondaryActionTextLight : null]}>{label}</Text>
    </Pressable>
  );
}

function Assurance({ icon: Icon, label }: { icon: DemoIcon; label: string }) {
  return (
    <View style={styles.assurance}>
      <Icon color={semantic.action.primary} size={16} strokeWidth={2} />
      <Text style={styles.assuranceText}>{label}</Text>
    </View>
  );
}

function PathCard({
  icon: Icon,
  title,
  body,
  action,
  featured,
  onPress,
}: {
  icon: DemoIcon;
  title: string;
  body: string;
  action: string;
  featured?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      onPress={onPress}
      style={[styles.pathCard, featured ? styles.pathCardFeatured : null]}
    >
      <View style={[styles.pathIcon, featured ? styles.pathIconFeatured : null]}>
        <Icon color={featured ? semantic.text.inverse : semantic.action.primary} size={23} strokeWidth={1.9} />
      </View>
      <Text style={styles.pathTitle}>{title}</Text>
      <Text style={styles.pathBody}>{body}</Text>
      <View style={styles.pathAction}>
        <Text style={styles.pathActionText}>{action}</Text>
        <ArrowRight color={semantic.action.primary} size={16} strokeWidth={2} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: semantic.surface.canvas,
  },
  scroll: {
    paddingBottom: 34,
  },
  shell: {
    width: "100%",
    maxWidth: 1180,
    alignSelf: "center",
    paddingHorizontal: 22,
    paddingTop: 14,
    gap: 26,
  },
  header: {
    minHeight: 90,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
  },
  headerMobile: {
    minHeight: 74,
    alignItems: "flex-start",
  },
  logoButton: {
    minHeight: 62,
    justifyContent: "center",
  },
  logo: {
    width: 250,
    height: 102,
  },
  nav: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 12,
  },
  navMobile: {
    flex: 1,
    justifyContent: "flex-end",
    gap: 8,
  },
  navItem: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  hero: {
    gap: 22,
  },
  heroDesktop: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  heroCopy: {
    flex: 1,
    minWidth: 300,
    justifyContent: "center",
    gap: 18,
    paddingVertical: 14,
  },
  careBadge: {
    alignSelf: "flex-start",
    minHeight: 34,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 13,
    borderRadius: 18,
    backgroundColor: semantic.overlay.forestSubtle,
  },
  careBadgeText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  heroTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 52,
    lineHeight: 55,
  },
  heroTitleDesktop: {
    fontSize: 72,
    lineHeight: 73,
    maxWidth: 620,
  },
  heroBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 17,
    lineHeight: 27,
    maxWidth: 650,
  },
  heroActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
  },
  primaryAction: {
    minHeight: 54,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    borderRadius: 27,
    paddingHorizontal: 22,
    backgroundColor: semantic.action.primary,
  },
  primaryActionLight: {
    backgroundColor: semantic.surface.raised,
  },
  primaryActionText: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
    lineHeight: 20,
  },
  primaryActionTextLight: {
    color: semantic.action.primary,
  },
  secondaryAction: {
    minHeight: 54,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    borderRadius: 27,
    paddingHorizontal: 18,
    borderWidth: 1,
    borderColor: semantic.overlay.forest,
    backgroundColor: semantic.surface.raised,
  },
  secondaryActionLight: {
    borderColor: semantic.overlay.inverseSubtle,
    backgroundColor: semantic.overlay.inverseSubtle,
  },
  secondaryActionText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  secondaryActionTextLight: {
    color: semantic.text.inverse,
  },
  assuranceRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 9,
  },
  assurance: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    borderRadius: 19,
    paddingHorizontal: 12,
    backgroundColor: semantic.overlay.inverseStrong,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  assuranceText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 17,
  },
  heroVisual: {
    flex: 0.86,
    minWidth: 310,
  },
  heroImage: {
    minHeight: 460,
    overflow: "hidden",
    justifyContent: "flex-end",
  },
  heroImageRadius: {
    borderRadius: 28,
  },
  heroImageScrim: {
    // StyleSheet.absoluteFillObject was removed from the RN 0.85 types.
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: semantic.overlay.darkSubtle,
  },
  serviceCard: {
    margin: 18,
    padding: 18,
    borderRadius: 20,
    backgroundColor: semantic.overlay.inverseStrong,
    gap: 6,
    ...shadows.soft,
  },
  serviceEyebrow: {
    color: semantic.accent.strong,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 15,
    textTransform: "uppercase",
  },
  serviceTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 30,
    lineHeight: 32,
  },
  serviceText: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 13,
    lineHeight: 20,
  },
  pathGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
  },
  pathCard: {
    flex: 1,
    minWidth: 250,
    gap: 12,
    padding: 18,
    borderRadius: 22,
    backgroundColor: semantic.surface.raised,
    borderWidth: 1,
    borderColor: semantic.overlay.line,
  },
  pathCardFeatured: {
    borderColor: semantic.overlay.forestStrong,
    ...shadows.soft,
  },
  pathIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: semantic.overlay.forestSubtle,
  },
  pathIconFeatured: {
    backgroundColor: semantic.action.primary,
  },
  pathTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 18,
    lineHeight: 24,
  },
  pathBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 22,
  },
  pathAction: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 2,
  },
  pathActionText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  splitBand: {
    gap: 20,
    padding: 22,
    borderRadius: 28,
    backgroundColor: semantic.surface.subtle,
  },
  splitBandDesktop: {
    flexDirection: "row",
    alignItems: "center",
    padding: 34,
  },
  bandCopy: {
    flex: 1,
    gap: 10,
  },
  sectionKicker: {
    color: semantic.accent.strong,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
    textTransform: "uppercase",
  },
  sectionTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 38,
    lineHeight: 40,
  },
  sectionBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 15,
    lineHeight: 24,
  },
  serviceList: {
    flex: 1,
    gap: 12,
    minWidth: 280,
  },
  serviceRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    padding: 13,
    borderRadius: 16,
    backgroundColor: semantic.overlay.inverse,
  },
  serviceRowText: {
    flex: 1,
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 14,
    lineHeight: 20,
  },
  contactBand: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 18,
    padding: 24,
    borderRadius: 28,
    backgroundColor: semantic.action.primary,
  },
  contactBandMobile: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  contactCopy: {
    flex: 1,
    gap: 6,
  },
  contactTitle: {
    color: semantic.text.inverse,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 33,
    lineHeight: 36,
  },
  contactText: {
    color: semantic.text.inverseMuted,
    fontFamily: "Manrope_500Medium",
    fontSize: 14,
    lineHeight: 22,
    maxWidth: 650,
  },
  contactActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
});
